import { useEffect, useState } from 'react';
import { FiGift, FiArrowRight, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { bonusPoolApi } from '../services/bonusPoolApi';

export default function BonusPoolInvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { code } = useParams();
  const { isAuthenticated, loading } = useAuth();
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const normalizedCode = (code || '').trim().toUpperCase();

  const mapInviteError = (raw) => {
    if (!raw) {
      return t('bonusPoolInvite.error');
    }
    const lower = String(raw).toLowerCase();
    if (lower.includes('own invite')) {
      return t('bonusPoolInvite.ownCode');
    }
    if (lower.includes('already used')) {
      return t('bonusPoolInvite.alreadyUsed');
    }
    if (lower.includes('inactive') || lower.includes('maxed out')) {
      return t('bonusPoolInvite.usedUp');
    }
    if (lower.includes('invalid invite code') || lower.includes('not found')) {
      return t('bonusPoolInvite.invalidCode');
    }
    return raw;
  };

  useEffect(() => {
    if (!normalizedCode) {
      setStatus('error');
      setMessage(t('bonusPoolInvite.invalidCode'));
      return;
    }
    if (!isAuthenticated || loading) {
      return;
    }
    let cancelled = false;
    const applyInvite = async () => {
      setStatus('loading');
      const result = await bonusPoolApi.applyInviteCode(normalizedCode);
      const responseCode = result.data?.code;
      const isSuccess =
        responseCode === 'SUCCESS' || responseCode === 0 || responseCode === '0';
      if (cancelled) {
        return;
      }
      if (result.ok && result.data && isSuccess) {
        setStatus('success');
        setMessage(t('bonusPoolInvite.successRedirect'));
        setTimeout(() => {
          navigate('/bonus-pool/rounds', { replace: true });
        }, 1500);
      } else {
        setStatus('error');
        setMessage(mapInviteError(result.data?.msg));
      }
    };
    applyInvite();
    return () => {
      cancelled = true;
    };
  }, [normalizedCode, isAuthenticated, loading, t, navigate]);

  const handleLogin = () => {
    navigate('/login', { state: { from: location } });
  };

  return (
    <div className="min-h-screen bg-surface text-slate-100">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-4 py-8 md:px-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-glass-border bg-surface/70 px-3 py-1 text-xs uppercase tracking-widest text-slate-400">
          <FiGift className="h-3.5 w-3.5 text-accent-soft" />
          {t('bonusPoolInvite.tag')}
        </div>

        <section className="glass-card p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500">
                {t('bonusPoolInvite.subtitle')}
              </div>
              <div className="mt-2 text-2xl font-semibold text-white">
                {t('bonusPoolInvite.title')}
              </div>
              <div className="mt-3 text-sm text-slate-300">
                {t('bonusPoolInvite.codeLabel')}: {normalizedCode || '-'}
              </div>
            </div>
            {isAuthenticated ? (
              <span className="inline-flex items-center gap-2 rounded-full border border-glass-border bg-surface/60 px-3 py-1 text-xs text-slate-300">
                <FiCheckCircle className="h-4 w-4 text-accent-soft" />
                {t('bonusPoolInvite.loggedIn')}
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 rounded-full border border-glass-border bg-surface/60 px-3 py-1 text-xs text-slate-300">
                <FiAlertCircle className="h-4 w-4 text-accent-warm" />
                {t('bonusPoolInvite.loginRequired')}
              </span>
            )}
          </div>

          <div className="mt-5 rounded-2xl border border-glass-border bg-surface/60 p-4 text-sm text-slate-300">
            {t('bonusPoolInvite.description')}
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            {!isAuthenticated && (
              <button
                onClick={handleLogin}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-accent-strong to-accent px-4 py-2 text-sm font-semibold text-white shadow-[0_10px_20px_rgba(8,12,18,0.35)] transition-transform hover:scale-[1.02]"
              >
                <FiArrowRight className="h-4 w-4" />
                {t('bonusPoolInvite.loginButton')}
              </button>
            )}
            <button
              onClick={() => navigate('/bonus-pool/rounds')}
              className="inline-flex items-center gap-2 rounded-xl border border-glass-border bg-surface/60 px-4 py-2 text-sm text-slate-200 transition-colors hover:bg-surface-highlight"
            >
              {t('bonusPoolInvite.goRounds')}
            </button>
          </div>

          <div className="mt-4 text-sm text-slate-300">
            {status === 'loading' && t('bonusPoolInvite.applying')}
            {status === 'success' && (
              <span className="inline-flex items-center gap-2 text-emerald-200">
                <FiCheckCircle className="h-4 w-4" />
                {message}
              </span>
            )}
            {status === 'error' && (
              <span className="inline-flex items-center gap-2 text-red-200">
                <FiAlertCircle className="h-4 w-4" />
                {message}
              </span>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
